'use client';
/**
 * Publications route. Canonical embedded URL: `/publications?embedded=true`
 * (and e.g. `?embedded=true&publicationId=...` for deep-linked search text).
 */
import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import withResourceAuth from '@/components/auth/withResourceAuth';
import { tableContainerStyle } from '@/styles/tableStyles';
import { PublicationSearchPanel } from '@/components/tables/search/PublicationSearchPanel';
import { PublicationGrid } from '@/components/tables/PublicationGrid';
import { defaultPublicationFilterCriteria, usePublicationFilterStore } from '@/stores/publicationFilterStore';
import { EMBEDDED_QUERY_PARAM, isEmbeddedQueryValue } from '@/lib/embeddedQuery';

interface PublicationsPageCoreProps {
  initialSearchText?: string;
  isEmbedded?: boolean;
}

export function PublicationsPageCore({ initialSearchText, isEmbedded = false }: PublicationsPageCoreProps) {
  const [refreshNonce, setRefreshNonce] = useState(0);
  const initializedSearchTextRef = useRef<string | null>(null);
  const filters = usePublicationFilterStore((state) => state.filters);
  const { setFilters, setPaginationModel } = usePublicationFilterStore();

  const normalizedInitialSearchText = useMemo(() => {
    const value = (initialSearchText ?? '').trim();
    return value.length > 0 ? value : '';
  }, [initialSearchText]);

  useEffect(() => {
    if (!normalizedInitialSearchText) return;
    if (initializedSearchTextRef.current === normalizedInitialSearchText) return;

    const alreadyInitialized =
      (filters.searchText || '') === normalizedInitialSearchText &&
      (filters.materials?.length || 0) === 0 &&
      (filters.elements?.length || 0) === 0 &&
      !filters.publicationType &&
      !filters.scienceDriver;

    if (alreadyInitialized) {
      initializedSearchTextRef.current = normalizedInitialSearchText;
      return;
    }

    setFilters({
      ...defaultPublicationFilterCriteria,
      searchText: normalizedInitialSearchText,
    });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [filters, normalizedInitialSearchText, setFilters, setPaginationModel]);

  const handlePublicationCreated = useCallback(() => {
    setRefreshNonce((prev) => prev + 1);
  }, []);

  return (
    <Box
      sx={{
        ...tableContainerStyle,
        display: 'flex',
        flexDirection: 'column',
        ...(isEmbedded
          ? {
              height: '100vh',
              flex: 1,
              minHeight: '100vh',
              width: '100%',
            }
          : {}),
      }}
    >
      <PublicationSearchPanel hideTitleRow={isEmbedded} onPublicationCreated={handlePublicationCreated} />
      <Box sx={{ width: '100%', flex: 1, minHeight: isEmbedded ? 0 : 420 }}>
        <PublicationGrid refreshNonce={refreshNonce} />
      </Box>
    </Box>
  );
}

function PublicationsPage() {
  const searchParams = useSearchParams();
  const publicationId = searchParams.get('publicationId') ?? undefined;
  const isEmbedded = isEmbeddedQueryValue(searchParams.get(EMBEDDED_QUERY_PARAM));
  return <PublicationsPageCore initialSearchText={publicationId} isEmbedded={isEmbedded} />;
}

export default withResourceAuth(PublicationsPage, {
  resourceType: 'publication',
  route: 'publications',
});
